import type { MarketCalendar } from "@/types/api";

/**
 * 장 상태 배지 문안 — 한 시장의 `GET /market/calendar/{country}` 응답 하나를 라벨·톤·꼬리말로.
 *
 * 배지가 국내·미국을 **구분해야 하는 이유**: 미국 정규장은 KST 로 22:30~05:00 이라, 국내장만
 * 보여주면 한국 새벽에 "장 마감" 하나만 떠 있고 실제로는 `market: US` 규칙이 돌고 있어요.
 * 어느 시장이 닫혔는지 말하지 않는 "장 마감" 은 운영자에게 틀린 정보입니다.
 *
 * 정규장이 아닌 개장 세션(장전·장후·주간거래)을 정규장과 **같은 색으로 그리지 않습니다**.
 * 규칙마다 허용 세션(`schedule.sessions`)이 달라서 이 시간대에는 평가되는 규칙과 그렇지 않은
 * 규칙이 섞이고, 서버는 그때 `SESSION_NOT_ALLOWED` 로 조용히 넘어갑니다.
 */
export type MarketTone = "positive" | "primary" | "neutral" | "cautionary";

export interface MarketStatusView {
  label: string;
  tone: MarketTone;
  /** 남은 시간. 개장 세션에서만 있어요. */
  tail: string | null;
  hint: string;
}

const MARKET_LABEL: Record<MarketCalendar["country"], string> = { KR: "국내", US: "미국" };

const SESSION_LABEL: Record<MarketCalendar["session"], string> = {
  PRE: "장전거래",
  REGULAR: "정규장",
  AFTER: "장후거래",
  DAY: "주간거래",
  CLOSED: "장 마감",
  unknown: "확인되지 않음",
};

/** 열려 있지만 정규장이 아닌 세션. */
const EXTENDED: readonly MarketCalendar["session"][] = ["PRE", "AFTER", "DAY"];

/**
 * 미국 시장은 표시 전용이에요 — 자동매매는 P1 입니다(`CLAUDE.md`). 배지가 국내와 나란히
 * 서면 "미국도 돌고 있다" 로 읽히니까 툴팁에서 한 번 끊어 줍니다.
 */
const MARKET_NOTE: Record<MarketCalendar["country"], string> = {
  KR: "",
  US: " 미국 주식 자동매매는 아직 지원하지 않아요 — 이 배지는 장 상태만 알려줘요.",
};

function duration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}시간 ${rest}분` : `${rest}분`;
}

function tailOf(calendar: MarketCalendar): string | null {
  if (calendar.minutes_to_close === null) return null;
  if (calendar.session === "REGULAR") return `마감까지 ${duration(calendar.minutes_to_close)}`;
  if (EXTENDED.includes(calendar.session)) return `종료까지 ${duration(calendar.minutes_to_close)}`;
  return null;
}

export function describeMarket(calendar: MarketCalendar): MarketStatusView {
  const market = MARKET_LABEL[calendar.country];
  const note = MARKET_NOTE[calendar.country];

  /*
    캘린더 장애를 휴장으로 위장하면 안 됩니다.

    `status_from` 은 CLOSED 를 네 사유로 만듭니다: `CALENDAR_MISSING`(오늘 문서 없음),
    `CALENDAR_STALE`(오늘 문서가 아님), `HOLIDAY`(정상 휴장), `OUTSIDE_SESSION`(장 시간 밖).
    앞의 둘은 **시장 잡이 전부 멈춘 상태**예요 — `protection.ensure`·`soft_watch`·
    `reregister_expiring` 까지 서지 않아서 무보호 포지션이 생길 수 있습니다. 이걸 휴장과 같은
    회색으로 그리면 운영자가 "휴일이니 당연하지" 로 넘겨 버립니다.
  */
  if (calendar.reason === "CALENDAR_MISSING" || calendar.reason === "CALENDAR_STALE") {
    return {
      label: `${market} 장 상태 확인 불가`,
      tone: "cautionary",
      tail: null,
      hint: `${market} 장 운영 정보를 받지 못했어요. 이 상태에서는 손절 재등록을 포함한 시장 잡이 전부 멈춰요. 운영 콘솔에서 확인해 주세요.${note}`,
    };
  }

  // 스펙이 unknown enum 을 허용해서(`CLAUDE.md`) 세션 이름도 모를 수 있어요. 모르는 상태를
  // 초록으로 그리지 않습니다 — fail-closed 가 기본입니다.
  if (calendar.session === "unknown") {
    return {
      label: `${market} 장 상태 확인되지 않음`,
      tone: "cautionary",
      tail: null,
      hint: `${market} 장 세션 이름을 알아보지 못했어요. 규칙이 평가되지 않을 수 있어요.${note}`,
    };
  }

  const holiday = !calendar.is_business_day;
  const closed = holiday || calendar.session === "CLOSED";
  const extended = EXTENDED.includes(calendar.session);

  const hint = holiday
    ? `${market} 휴장일이에요. 규칙이 평가되지 않아요.${note}`
    : closed
      ? `${market} 장 운영 시간이 아니에요. 규칙은 다음 개장에 다시 평가돼요.${note}`
      : extended
        ? `${market} 정규장이 아니에요. 규칙마다 허용 세션이 달라서, 정규장만 허용한 규칙은 이 시간대에 평가되지 않아요.${note}`
        : `${market} 정규장이에요. 장 운영 시간은 토스증권이 알려주는 값이에요.${note}`;

  return {
    label: `${market} ${SESSION_LABEL[calendar.session]}`,
    tone: closed ? "neutral" : extended ? "primary" : "positive",
    tail: tailOf(calendar),
    hint,
  };
}
